import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Package, ClipboardList, Tags, Award, Settings as SettingsIcon, LogOut,
    Plus, Pencil, Trash2, X, Upload, ChevronDown, Mail
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { fmtPrice } from "../data/site";

const STATUSES = ["New", "Confirmed", "Processing", "Delivered", "Cancelled"];
const statusColor = {
    New: "bg-secondary text-secondary-foreground", Confirmed: "bg-blue-600 text-white",
    Processing: "bg-amber-500 text-white", Delivered: "bg-primary text-primary-foreground",
    Cancelled: "bg-destructive text-white",
};

const fileToBase64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
});

const TABS = [
    { id: "orders", label: "Orders", icon: ClipboardList },
    { id: "products", label: "Products", icon: Package },
    { id: "categories", label: "Categories", icon: Tags },
    { id: "brands", label: "Brands", icon: Award },
    { id: "messages", label: "Messages", icon: Mail },
    { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminDashboard() {
    const { admin, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("orders");

    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [messages, setMessages] = useState([]);
    const [settings, setSettings] = useState({});

    const refresh = useCallback(() => {
        api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
        api.get("/products").then((r) => setProducts(r.data));
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/brands").then((r) => setBrands(r.data));
        api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
        api.get("/settings").then((r) => setSettings(r.data));
    }, []);

    useEffect(() => {
        if (!loading && !admin) navigate("/admin");
        if (admin) refresh();
    }, [admin, loading, navigate, refresh]);

    if (loading || !admin) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

    return (
        <div className="min-h-screen bg-muted" data-testid="admin-dashboard">
            <header className="bg-foreground text-background sticky top-0 z-40">
                <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-secondary text-secondary-foreground flex items-center justify-center font-heading font-black">Y</div>
                        <span className="font-heading font-bold">YATTO Admin</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-background/60 hidden sm:block">{admin.email}</span>
                        <button onClick={() => { logout(); navigate("/admin"); }} className="flex items-center gap-2 text-sm hover:text-secondary transition-colors" data-testid="admin-logout">
                            <LogOut size={16} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex">
                <nav className="hidden md:flex flex-col w-56 bg-background border-r border-border min-h-[calc(100vh-4rem)] p-3">
                    {TABS.map((t) => (
                        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium text-left transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                            <t.icon size={17} /> {t.label}
                        </button>
                    ))}
                </nav>

                <main className="flex-1 p-4 sm:p-8 min-w-0">
                    <div className="md:hidden flex gap-2 overflow-x-auto mb-6">
                        {TABS.map((t) => (
                            <button key={t.id} onClick={() => setTab(t.id)} className={`whitespace-nowrap px-4 py-2 text-sm border ${tab === t.id ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{t.label}</button>
                        ))}
                    </div>

                    {tab === "orders" && <OrdersTab orders={orders} refresh={refresh} />}
                    {tab === "products" && <ProductsTab products={products} categories={categories} brands={brands} refresh={refresh} />}
                    {tab === "categories" && <CategoriesTab categories={categories} refresh={refresh} />}
                    {tab === "brands" && <BrandsTab brands={brands} refresh={refresh} />}
                    {tab === "messages" && <MessagesTab messages={messages} />}
                    {tab === "settings" && <SettingsTab settings={settings} setSettings={setSettings} refresh={refresh} />}
                </main>
            </div>
        </div>
    );
}

/* ---------------- Orders ---------------- */
function OrdersTab({ orders, refresh }) {
    const [expanded, setExpanded] = useState(null);
    const setStatus = async (id, status) => {
        await api.put(`/admin/orders/${id}/status`, { status });
        toast.success(`Order marked ${status}`);
        refresh();
    };
    const del = async (id) => {
        if (!window.confirm("Delete this order?")) return;
        await api.delete(`/admin/orders/${id}`);
        toast.success("Order deleted");
        refresh();
    };

    return (
        <div>
            <h1 className="font-heading font-bold text-3xl mb-6">Orders <span className="text-muted-foreground text-lg">({orders.length})</span></h1>
            {orders.length === 0 ? <Empty text="No order requests yet." /> : (
                <div className="space-y-3">
                    {orders.map((o) => (
                        <div key={o.id} className="bg-background border border-border" data-testid={`order-row-${o.id}`}>
                            <div className="p-4 flex flex-wrap items-center gap-4 cursor-pointer" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                                <div className="flex-1 min-w-[200px]">
                                    <p className="font-heading font-bold">{o.business_name}</p>
                                    <p className="text-sm text-muted-foreground">{o.customer_name} · {o.phone} · {o.email}</p>
                                </div>
                                <div className="text-sm text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</div>
                                <span className={`text-xs font-bold uppercase px-3 py-1.5 ${statusColor[o.status]}`}>{o.status}</span>
                                <span className="text-sm font-semibold">{o.items?.length} item(s)</span>
                                <ChevronDown size={18} className={`transition-transform ${expanded === o.id ? "rotate-180" : ""}`} />
                            </div>
                            {expanded === o.id && (
                                <div className="border-t border-border p-4 bg-muted/40">
                                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4">
                                        <p><b>Business Type:</b> {o.business_type || "—"}</p>
                                        <p><b>Wholesale Account:</b> {o.request_account ? "Requested" : "No"}</p>
                                        <p className="sm:col-span-2"><b>Delivery:</b> {o.delivery_address}, {o.city}, {o.state} {o.zip_code}</p>
                                        <p><b>Preferred Date:</b> {o.preferred_date || "—"}</p>
                                        <p className="sm:col-span-2"><b>Notes:</b> {o.notes || "—"}</p>
                                    </div>
                                    <table className="w-full text-sm border border-border mb-4 bg-background">
                                        <thead className="bg-muted"><tr><th className="text-left p-2">Product</th><th className="text-left p-2">Brand</th><th className="text-left p-2">Unit</th><th className="text-left p-2">Cases</th></tr></thead>
                                        <tbody>
                                            {o.items?.map((it, idx) => (
                                                <tr key={idx} className="border-t border-border"><td className="p-2">{it.name}</td><td className="p-2">{it.brand}</td><td className="p-2">{it.unit_size}</td><td className="p-2 font-semibold">{it.cases}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs uppercase tracking-wider text-muted-foreground mr-2">Set status:</span>
                                        {STATUSES.map((s) => (
                                            <button key={s} onClick={() => setStatus(o.id, s)} data-testid={`set-status-${o.id}-${s}`} className={`text-xs px-3 py-1.5 border transition-colors ${o.status === s ? statusColor[s] : "border-border hover:border-primary"}`}>{s}</button>
                                        ))}
                                        <button onClick={() => del(o.id)} className="ml-auto text-destructive flex items-center gap-1 text-sm" data-testid={`delete-order-${o.id}`}><Trash2 size={15} /> Delete</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------------- Products ---------------- */
const emptyProduct = {
    name: "", brand: "", category: "", product_type: "", image: "", unit_size: "", units_per_case: "",
    packaging: "", wholesale_price_per_case: "", price_per_unit: "", suggested_retail: "", origin: "",
    certifications: "", flavors: "", notes: "", in_stock: true, limited_time: false, featured: false,
};

function ProductsTab({ products, categories, brands, refresh }) {
    const [editing, setEditing] = useState(null);

    const save = async (form) => {
        const payload = {
            ...form,
            wholesale_price_per_case: form.wholesale_price_per_case === "" ? null : Number(form.wholesale_price_per_case),
            price_per_unit: form.price_per_unit === "" ? null : Number(form.price_per_unit),
            suggested_retail: form.suggested_retail === "" ? null : Number(form.suggested_retail),
            flavors: typeof form.flavors === "string" ? form.flavors.split(",").map((s) => s.trim()).filter(Boolean) : form.flavors,
        };
        if (form.id) await api.put(`/admin/products/${form.id}`, payload);
        else await api.post("/admin/products", payload);
        toast.success("Product saved");
        setEditing(null);
        refresh();
    };
    const del = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        await api.delete(`/admin/products/${id}`);
        toast.success("Product deleted");
        refresh();
    };
    const quickToggle = async (p, field) => {
        const payload = { ...p, [field]: !p[field], flavors: p.flavors || [] };
        delete payload.id; delete payload.created_at;
        await api.put(`/admin/products/${p.id}`, payload);
        refresh();
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-heading font-bold text-3xl">Products <span className="text-muted-foreground text-lg">({products.length})</span></h1>
                <button onClick={() => setEditing({ ...emptyProduct })} data-testid="add-product-btn" className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 font-semibold text-sm hover:bg-secondary hover:text-secondary-foreground transition-colors"><Plus size={16} /> Add Product</button>
            </div>
            <div className="bg-background border border-border overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]">
                    <thead className="bg-muted"><tr>
                        <th className="text-left p-3">Product</th><th className="text-left p-3">Brand</th><th className="text-left p-3">Category</th>
                        <th className="text-left p-3">Case $</th><th className="text-left p-3">Flags</th><th className="text-right p-3">Actions</th>
                    </tr></thead>
                    <tbody>
                        {products.map((p) => (
                            <tr key={p.id} className="border-t border-border" data-testid={`product-row-${p.id}`}>
                                <td className="p-3 flex items-center gap-3">{p.image && <img src={p.image} alt="" className="w-9 h-9 object-cover" />}<span className="font-medium">{p.name} <span className="text-muted-foreground">{p.unit_size}</span></span></td>
                                <td className="p-3">{p.brand}</td>
                                <td className="p-3 text-muted-foreground">{p.category}</td>
                                <td className="p-3 font-semibold">{fmtPrice(p.wholesale_price_per_case) || "—"}</td>
                                <td className="p-3">
                                    <div className="flex gap-1">
                                        <button onClick={() => quickToggle(p, "in_stock")} className={`text-[10px] px-2 py-1 ${p.in_stock ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`} title="Stock">{p.in_stock ? "In Stock" : "Out"}</button>
                                        <button onClick={() => quickToggle(p, "limited_time")} className={`text-[10px] px-2 py-1 ${p.limited_time ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground"}`} title="Limited">LTO</button>
                                        <button onClick={() => quickToggle(p, "featured")} className={`text-[10px] px-2 py-1 ${p.featured ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`} title="Featured">★</button>
                                    </div>
                                </td>
                                <td className="p-3 text-right">
                                    <button onClick={() => setEditing({ ...p, flavors: (p.flavors || []).join(", ") })} className="text-primary p-1" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>
                                    <button onClick={() => del(p.id)} className="text-destructive p-1" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {editing && <ProductModal initial={editing} categories={categories} brands={brands} onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function ProductModal({ initial, categories, brands, onClose, onSave }) {
    const [form, setForm] = useState(initial);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const onImage = async (e) => {
        const file = e.target.files[0];
        if (file) set("image", await fileToBase64(file));
    };
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try { await onSave(form); } catch { toast.error("Save failed"); } finally { setBusy(false); }
    };
    const cls = "w-full border border-border bg-transparent px-3 py-2 text-sm focus:outline-none focus:border-primary";
    return (
        <Modal title={form.id ? "Edit Product" : "Add Product"} onClose={onClose}>
            <form onSubmit={submit} className="space-y-4" data-testid="product-form">
                <Field label="Product Name *"><input className={cls} required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" /></Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Brand"><input className={cls} list="brands-list" value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="pf-brand" /><datalist id="brands-list">{brands.map((b) => <option key={b.id} value={b.name} />)}</datalist></Field>
                    <Field label="Category"><select className={cls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category"><option value="">Select</option>{categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</select></Field>
                    <Field label="Unit Size"><input className={cls} value={form.unit_size} onChange={(e) => set("unit_size", e.target.value)} /></Field>
                    <Field label="Units per Case"><input className={cls} value={form.units_per_case} onChange={(e) => set("units_per_case", e.target.value)} /></Field>
                    <Field label="Packaging"><input className={cls} value={form.packaging} onChange={(e) => set("packaging", e.target.value)} /></Field>
                    <Field label="Product Type"><input className={cls} value={form.product_type} onChange={(e) => set("product_type", e.target.value)} /></Field>
                    <Field label="Wholesale $/case"><input type="number" step="0.01" className={cls} value={form.wholesale_price_per_case} onChange={(e) => set("wholesale_price_per_case", e.target.value)} data-testid="pf-wholesale" /></Field>
                    <Field label="Price/unit"><input type="number" step="0.01" className={cls} value={form.price_per_unit} onChange={(e) => set("price_per_unit", e.target.value)} /></Field>
                    <Field label="Suggested Retail"><input type="number" step="0.01" className={cls} value={form.suggested_retail} onChange={(e) => set("suggested_retail", e.target.value)} /></Field>
                    <Field label="Country of Origin"><input className={cls} value={form.origin} onChange={(e) => set("origin", e.target.value)} /></Field>
                    <Field label="Certifications"><input className={cls} value={form.certifications} onChange={(e) => set("certifications", e.target.value)} /></Field>
                </div>
                <Field label="Flavors (comma separated)"><input className={cls} value={form.flavors} onChange={(e) => set("flavors", e.target.value)} /></Field>
                <Field label="Notes"><textarea rows={2} className={cls} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
                <Field label="Image">
                    <div className="flex items-center gap-3">
                        {form.image && <img src={form.image} alt="" className="w-14 h-14 object-cover" />}
                        <label className="flex items-center gap-2 border border-border px-3 py-2 text-sm cursor-pointer hover:border-primary"><Upload size={15} /> Upload<input type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="pf-image" /></label>
                        <input className={cls} placeholder="or paste image URL" value={form.image} onChange={(e) => set("image", e.target.value)} />
                    </div>
                </Field>
                <div className="flex flex-wrap gap-4">
                    <Toggle label="In Stock" checked={form.in_stock} onChange={(v) => set("in_stock", v)} />
                    <Toggle label="Limited-Time Offer" checked={form.limited_time} onChange={(v) => set("limited_time", v)} />
                    <Toggle label="Featured" checked={form.featured} onChange={(v) => set("featured", v)} />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border text-sm font-semibold">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="save-product" className="px-6 py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors">{busy ? "Saving…" : "Save Product"}</button>
                </div>
            </form>
        </Modal>
    );
}

/* ---------------- Categories ---------------- */
function CategoriesTab({ categories, refresh }) {
    const [editing, setEditing] = useState(null);
    const save = async (form) => {
        if (form.id) await api.put(`/admin/categories/${form.id}`, form);
        else await api.post("/admin/categories", form);
        toast.success("Category saved"); setEditing(null); refresh();
    };
    const del = async (id) => { if (!window.confirm("Delete category?")) return; await api.delete(`/admin/categories/${id}`); toast.success("Deleted"); refresh(); };
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-heading font-bold text-3xl">Categories</h1>
                <button onClick={() => setEditing({ name: "", description: "", image: "", order: categories.length + 1 })} data-testid="add-category-btn" className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 font-semibold text-sm"><Plus size={16} /> Add</button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((c) => (
                    <div key={c.id} className="bg-background border border-border p-4 flex gap-3" data-testid={`category-row-${c.id}`}>
                        {c.image && <img src={c.image} alt="" className="w-16 h-16 object-cover" />}
                        <div className="flex-1 min-w-0">
                            <p className="font-heading font-bold">{c.name}</p>
                            <p className="text-xs text-muted-foreground line-clamp-2">{c.description}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                            <button onClick={() => setEditing(c)} className="text-primary"><Pencil size={15} /></button>
                            <button onClick={() => del(c.id)} className="text-destructive"><Trash2 size={15} /></button>
                        </div>
                    </div>
                ))}
            </div>
            {editing && <CatBrandModal initial={editing} type="category" onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

/* ---------------- Brands ---------------- */
function BrandsTab({ brands, refresh }) {
    const [editing, setEditing] = useState(null);
    const save = async (form) => {
        if (form.id) await api.put(`/admin/brands/${form.id}`, form);
        else await api.post("/admin/brands", form);
        toast.success("Brand saved"); setEditing(null); refresh();
    };
    const del = async (id) => { if (!window.confirm("Delete brand?")) return; await api.delete(`/admin/brands/${id}`); toast.success("Deleted"); refresh(); };
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-heading font-bold text-3xl">Brands</h1>
                <button onClick={() => setEditing({ name: "", origin: "", description: "", logo: "" })} data-testid="add-brand-btn" className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 font-semibold text-sm"><Plus size={16} /> Add</button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {brands.map((b) => (
                    <div key={b.id} className="bg-background border border-border p-5" data-testid={`brand-row-${b.id}`}>
                        <div className="flex items-center justify-between mb-2">
                            <p className="font-heading font-bold text-lg">{b.name}</p>
                            <div className="flex gap-2">
                                <button onClick={() => setEditing(b)} className="text-primary"><Pencil size={15} /></button>
                                <button onClick={() => del(b.id)} className="text-destructive"><Trash2 size={15} /></button>
                            </div>
                        </div>
                        <p className="text-xs uppercase tracking-wider text-secondary font-semibold mb-2">{b.origin}</p>
                        <p className="text-sm text-muted-foreground">{b.description}</p>
                    </div>
                ))}
            </div>
            {editing && <CatBrandModal initial={editing} type="brand" onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function CatBrandModal({ initial, type, onClose, onSave }) {
    const [form, setForm] = useState(initial);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const onImage = async (e) => { const f = e.target.files[0]; if (f) set(type === "category" ? "image" : "logo", await fileToBase64(f)); };
    const submit = async (e) => { e.preventDefault(); setBusy(true); try { await onSave(form); } catch { toast.error("Failed"); } finally { setBusy(false); } };
    const cls = "w-full border border-border bg-transparent px-3 py-2 text-sm focus:outline-none focus:border-primary";
    return (
        <Modal title={form.id ? `Edit ${type}` : `Add ${type}`} onClose={onClose}>
            <form onSubmit={submit} className="space-y-4" data-testid={`${type}-form`}>
                <Field label="Name *"><input className={cls} required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid={`${type}f-name`} /></Field>
                {type === "category" && <Field label="Display Order"><input type="number" className={cls} value={form.order || 0} onChange={(e) => set("order", Number(e.target.value))} /></Field>}
                {type === "brand" && <Field label="Origin"><input className={cls} value={form.origin || ""} onChange={(e) => set("origin", e.target.value)} /></Field>}
                <Field label="Description"><textarea rows={3} className={cls} value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></Field>
                <Field label={type === "category" ? "Image" : "Logo"}>
                    <div className="flex items-center gap-3">
                        {(form.image || form.logo) && <img src={form.image || form.logo} alt="" className="w-14 h-14 object-cover" />}
                        <label className="flex items-center gap-2 border border-border px-3 py-2 text-sm cursor-pointer hover:border-primary"><Upload size={15} /> Upload<input type="file" accept="image/*" className="hidden" onChange={onImage} /></label>
                    </div>
                </Field>
                <div className="flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border text-sm font-semibold">Cancel</button>
                    <button type="submit" disabled={busy} data-testid={`save-${type}`} className="px-6 py-2.5 bg-primary text-primary-foreground text-sm font-semibold">{busy ? "Saving…" : "Save"}</button>
                </div>
            </form>
        </Modal>
    );
}

/* ---------------- Messages ---------------- */
function MessagesTab({ messages }) {
    return (
        <div>
            <h1 className="font-heading font-bold text-3xl mb-6">Messages <span className="text-muted-foreground text-lg">({messages.length})</span></h1>
            {messages.length === 0 ? <Empty text="No contact messages yet." /> : (
                <div className="space-y-3">
                    {messages.map((m) => (
                        <div key={m.id} className="bg-background border border-border p-5" data-testid={`message-row-${m.id}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <p className="font-heading font-bold">{m.name} {m.business_name && <span className="text-muted-foreground font-body font-normal">· {m.business_name}</span>}</p>
                                <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-1">{m.email} {m.phone && `· ${m.phone}`}</p>
                            {m.interested_products && <p className="text-sm mb-2"><b>Interested:</b> {m.interested_products}</p>}
                            <p className="text-sm">{m.message}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------------- Settings ---------------- */
function SettingsTab({ settings, setSettings, refresh }) {
    const [busy, setBusy] = useState(false);
    const update = async (patch) => {
        setBusy(true);
        try {
            const { data } = await api.put("/admin/settings", patch);
            setSettings(data); toast.success("Settings updated"); refresh();
        } catch { toast.error("Update failed"); } finally { setBusy(false); }
    };
    const onPdf = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const b64 = await fileToBase64(file);
        update({ catalog_pdf: b64, catalog_filename: file.name });
    };
    return (
        <div className="max-w-2xl">
            <h1 className="font-heading font-bold text-3xl mb-6">Settings</h1>
            <div className="bg-background border border-border divide-y divide-border">
                <SettingRow title="Show Wholesale Prices" desc="When off, products show 'Contact for Wholesale Pricing'.">
                    <Toggle checked={settings.show_wholesale_prices !== false} onChange={(v) => update({ show_wholesale_prices: v })} data-testid="toggle-wholesale" />
                </SettingRow>
                <SettingRow title="Show Suggested Retail Prices" desc="Display the suggested retail price on product cards.">
                    <Toggle checked={settings.show_retail_prices !== false} onChange={(v) => update({ show_retail_prices: v })} data-testid="toggle-retail" />
                </SettingRow>
                <SettingRow title="Catalog PDF" desc={settings.catalog_filename ? `Current: ${settings.catalog_filename}` : "Upload a PDF for customers to download."}>
                    <div className="flex items-center gap-2">
                        {settings.catalog_pdf && <a href={settings.catalog_pdf} download={settings.catalog_filename} className="text-primary text-sm font-semibold">View</a>}
                        <label className="flex items-center gap-2 border border-border px-3 py-2 text-sm cursor-pointer hover:border-primary"><Upload size={15} /> Upload<input type="file" accept="application/pdf" className="hidden" onChange={onPdf} data-testid="catalog-upload" /></label>
                        {settings.catalog_pdf && <button onClick={() => update({ catalog_pdf: "", catalog_filename: "" })} className="text-destructive text-sm">Remove</button>}
                    </div>
                </SettingRow>
            </div>
            {busy && <p className="text-sm text-muted-foreground mt-3">Saving…</p>}
        </div>
    );
}

/* ---------------- Shared UI ---------------- */
const Empty = ({ text }) => <div className="bg-background border border-dashed border-border p-16 text-center text-muted-foreground">{text}</div>;
const Field = ({ label, children }) => <label className="block"><span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>{children}</label>;
const SettingRow = ({ title, desc, children }) => (
    <div className="p-5 flex items-center justify-between gap-4">
        <div><p className="font-semibold">{title}</p><p className="text-sm text-muted-foreground">{desc}</p></div>
        <div className="shrink-0">{children}</div>
    </div>
);
const Toggle = ({ label, checked, onChange, ...rest }) => (
    <label className="flex items-center gap-2 cursor-pointer select-none">
        <button type="button" onClick={() => onChange(!checked)} className={`w-11 h-6 flex items-center transition-colors ${checked ? "bg-primary" : "bg-muted-foreground/30"}`} {...rest}>
            <span className={`w-5 h-5 bg-background transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
        {label && <span className="text-sm">{label}</span>}
    </label>
);
const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 z-50 bg-foreground/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
        <div className="bg-background w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-background">
                <h2 className="font-heading font-bold text-xl">{title}</h2>
                <button onClick={onClose} className="p-1 hover:text-destructive"><X size={20} /></button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>
);
